/**
 * Hooks de FORMAS DE PAGAMENTO (payment method configs) — espelham
 * `PaymentMethodConfigResponseDto` de treasury.dto.ts.
 *
 * Contratos consumidos (served-routes.json):
 *   GET    /finance/payment-methods?isActive&q&page&pageSize → PaginatedResponseDto<PaymentMethodConfigResponseDto>
 *   POST   /finance/payment-methods                          → PaymentMethodConfigResponseDto (PaymentMethodConfigInputDto)
 *   PUT    /finance/payment-methods/:id                      → PaymentMethodConfigResponseDto (PaymentMethodConfigUpdateInputDto)
 *   DELETE /finance/payment-methods/:id                      → { id, deleted }
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import type { PaginatedResponse } from './treasury/treasuryTypes';

/** treasury.dto.ts: PaymentMethodConfigResponseDto */
export interface PaymentMethodConfig {
  id: string;
  code: string;
  name: string;
  type: string;
  feeBaseType: string;
  feeBaseValue: number;
  settlementDays: number;
  monthlyVolume: number;
  maxInstallments: number;
  allowChange: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** treasury.dto.ts: PaymentMethodConfigInputDto (corpo do POST /finance/payment-methods) */
export interface PaymentMethodConfigInput {
  code: string;
  name: string;
  type: string;
  feeBaseType?: string;
  feeBaseValue?: number;
  settlementDays?: number;
  monthlyVolume?: number;
  maxInstallments?: number;
  allowChange?: boolean;
}

/** treasury.dto.ts: PaymentMethodConfigUpdateInputDto (corpo do PUT /finance/payment-methods/:id) */
export type PaymentMethodConfigUpdateInput = Partial<
  Pick<
    PaymentMethodConfigInput,
    'name' | 'feeBaseType' | 'feeBaseValue' | 'settlementDays' | 'monthlyVolume' | 'maxInstallments' | 'allowChange'
  >
> & {
  isActive?: boolean;
};

/** treasury.dto.ts: ListParamsDto (query de GET /finance/payment-methods) */
export interface PaymentMethodFilters {
  isActive?: boolean;
  q?: string;
  page?: number;
  pageSize?: number;
}

const paymentMethodsKeys = {
  list: (filters: PaymentMethodFilters) => ['finance', 'treasury', 'payment-methods', filters] as const,
};

export function usePaymentMethods(filters: PaymentMethodFilters = {}) {
  return useQuery({
    queryKey: paymentMethodsKeys.list(filters),
    queryFn: () =>
      apiClient.get<PaginatedResponse<PaymentMethodConfig>>('/finance/payment-methods', {
        params: {
          page: String(filters.page ?? 1),
          pageSize: String(filters.pageSize ?? 50),
          ...(filters.isActive !== undefined ? { isActive: String(filters.isActive) } : {}),
          ...(filters.q ? { q: filters.q } : {}),
        },
      }),
  });
}

export function useCreatePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PaymentMethodConfigInput) =>
      apiClient.post<PaymentMethodConfig>('/finance/payment-methods', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'payment-methods'] });
    },
  });
}

export function useUpdatePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & PaymentMethodConfigUpdateInput) =>
      apiClient.put<PaymentMethodConfig>(`/finance/payment-methods/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'payment-methods'] });
    },
  });
}

export function useDeletePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<{ id: string; deleted: boolean }>(`/finance/payment-methods/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'payment-methods'] });
    },
  });
}

/* ── Rótulos de apresentação (derivados) ── */

export const PAYMENT_METHOD_TYPE_LABEL: Record<string, string> = {
  MONEY: 'Dinheiro',
  CREDIT_CARD: 'Cartão de crédito',
  DEBIT_CARD: 'Cartão de débito',
  PIX: 'Pix',
  BOLETO: 'Boleto bancário',
  TRANSFER: 'Transferência',
  CHECK: 'Cheque',
  OTHER: 'Outro',
};

export const FEE_BASE_TYPE_LABEL: Record<string, string> = {
  PERCENTAGE: '%',
  FIXED: 'R$',
};

/** Formata a taxa base conforme o tipo: percentual ou fixa. */
export function formatFee(feeBaseType: string, feeBaseValue: number): string {
  if (feeBaseType === 'PERCENTAGE') return `${feeBaseValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  if (feeBaseType === 'FIXED' && feeBaseValue > 0) return `R$ ${feeBaseValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return '0,00%';
}

/** Liquidação em D+n a partir de settlementDays. */
export function formatSettlement(days: number): string {
  if (days <= 0) return 'D+0';
  return `D+${days}`;
}